/**
 * @file ProteinuriaDetailListView.jsx
 * @author Daniel Aguirre
 * @description Dashboard Detail Management View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Helpers
import { useParams } from 'react-router';
import {
  formatDate,
  joinArrayObject,
  removeNullValuesFromObject,
  sortStringPlus
} from 'commons/helpers/utils';
import {
  createColumns,
  getSearchApiKeys,
  getSelectFilter,
  getSorterString,
  setBadgeVariationProteinuria,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function ProteinuriaDetailListView({ dashboardStaffActions, clinicId }) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { proteinuriaDetail, totalItems: totalItemsParams } = useParams();
  const [totalItems, setTotalItems] = useState(totalItemsParams);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const { getProteinuriaListPlus } = dashboardStaffActions;

  const columnVariation = {
    title: translate('dashboardStaff.variation'),
    dataIndex: 'variacion',
    filter: {
      listName: 'lista_variacion',
      searchName: 'listaVariacion',
      type: 'select'
    },
    sorter: {
      compare: sortStringPlus('variacion'),
      multiple: 16
    },
    ...getSelectFilter(
      {
        filter: {
          listName: 'lista_variacion',
          searchName: 'listaVariacion'
        },
        dataIndex: 'variacion'
      },
      dataFilter
    ),
    ellipsis: true
  };

  const additionalColumns = [
    {
      title: translate('dashboardStaff.proteinuriaPenultimateScore'),
      dataIndex: 'penultima_proteinuria',
      filter: {
        listName: 'lista_resultado_penultima_proteinuria_24h',
        searchName: 'listaResultadoPenultimaProteinuria24H',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('penultima_proteinuria'),
        multiple: 16
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_resultado_penultima_proteinuria_24h',
            searchName: 'listaResultadoPenultimaProteinuria24H'
          },
          dataIndex: 'penultima_proteinuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.proteinuriaPenultimateDate'),
      dataIndex: 'fecha_penultima_proteinuria',
      filter: {
        listName: 'lista_fecha_penultima_proteinuria_24h',
        searchName: 'listaFechaPenultimaProteinuria24H',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_penultima_proteinuria'),
        multiple: 17
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_penultima_proteinuria_24h',
            searchName: 'listaFechaPenultimaProteinuria24H'
          },
          dataIndex: 'fecha_penultima_proteinuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.proteinuriaLastScore'),
      dataIndex: 'ultima_proteinuria',
      filter: {
        listName: 'lista_resultado_ultima_proteinuria_24h',
        searchName: 'listaResultadoUltimaProteinuria24H',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('ultima_proteinuria'),
        multiple: 18
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_resultado_ultima_proteinuria_24h',
            searchName: 'listaResultadoUltimaProteinuria24H'
          },
          dataIndex: 'ultima_proteinuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.proteinuriaLastDate'),
      dataIndex: 'fecha_ultima_proteinuria',
      filter: {
        listName: 'lista_fecha_ultima_proteinuria_24h',
        searchName: 'listaFechaUltimaProteinuria24H',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('fecha_ultima_proteinuria'),
        multiple: 19
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_fecha_ultima_proteinuria_24h',
            searchName: 'listaFechaUltimaProteinuria24H'
          },
          dataIndex: 'fecha_ultima_proteinuria'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.diabetesDiagnostic'),
      dataIndex: 'diagnostico_diabetes',
      filter: {
        listName: 'lista_diagnostico_diabetes',
        searchName: 'listaDiagnosticoDiabetes',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_diabetes'),
        multiple: 20
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_diabetes',
            searchName: 'listaDiagnosticoDiabetes'
          },
          dataIndex: 'diagnostico_diabetes'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.hipertensionDiagnostic'),
      dataIndex: 'diagnostico_hipertension',
      filter: {
        listName: 'lista_diagnostico_hipertension',
        searchName: 'listaDiagnosticoHipertension',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_hipertension'),
        multiple: 21
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_hipertension',
            searchName: 'listaDiagnosticoHipertension'
          },
          dataIndex: 'diagnostico_hipertension'
        },
        dataFilter
      ),
      ellipsis: true
    },
    {
      title: translate('dashboardStaff.glomerularDiagnostic'),
      dataIndex: 'diagnostico_enfermedad_glomerular',
      filter: {
        listName: 'lista_diagnostico_enfermedad_glomerular',
        searchName: 'listaDiagnosticoEnfermedadGlomerular',
        type: 'select'
      },
      sorter: {
        compare: sortStringPlus('diagnostico_enfermedad_glomerular'),
        multiple: 22
      },
      ...getSelectFilter(
        {
          filter: {
            listName: 'lista_diagnostico_enfermedad_glomerular',
            searchName: 'listaDiagnosticoEnfermedadGlomerular'
          },
          dataIndex: 'diagnostico_enfermedad_glomerular'
        },
        dataFilter
      ),
      ellipsis: true
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        idClinic: clinicId,
        controlSeleccionado: proteinuriaDetail,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getProteinuriaListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        const newRowData = resp?.data?.listaDetalles?.map((item) => {
          return {
            ...item,
            variacion: setBadgeVariationProteinuria(item.variacion),
            fecha_ultima_proteinuria: item?.fecha_ultima_proteinuria
              ? formatDate(item.fecha_ultima_proteinuria)
              : '',
            fecha_penultima_proteinuria: item?.fecha_penultima_proteinuria
              ? formatDate(item.fecha_penultima_proteinuria)
              : ''
          };
        });
        setTotalItems(resp.data?.totalRegistros);
        setRowData(newRowData ?? []);
        setDataFilter(resp.data);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    setColumns(cols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        [columnVariation, ...columns, ...additionalColumns],
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={[columnVariation, ...columns]}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      additionalColumns={additionalColumns}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
ProteinuriaDetailListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getProteinuriaListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default ProteinuriaDetailListView;
